// projects — the author's projects as the lists show them, projected from
// the Library, pure (pen "Projects and the Studio: in and out", All projects).
//
// A project (Studio v1 D1) holds materials and sources by reference on its
// canvas; a container (course, module) holds its parts. Walking that tree,
// each project says:
//  - its form: one course is the whole content ("course"), anything else
//    has no fixed form ("free");
//  - how many modules and pages it holds (a page: any material that is not a
//    container; sources are not material and are not counted);
//  - its outline in miniature: the root's first parts (the course's parts,
//    or what lies loose on the canvas), for the card's preview;
//  - when it last changed: the newest edit (or creation) in its tree;
//  - what waits for the author: open suggestions (the Library's proposals)
//    in its tree;
//  - where the author was in it (projectPlaces.ts): the page shown when they
//    left, if the project still holds it.
// The walk is bounded: each material is visited once (a cycle ends there)
// and at most TREE_MAX are visited per project. Complexity: O(materials) per
// project.

export type { ProjectForm, ProjectSummary };
export { projectsOf };

import type { Library, MaterialItem } from "../../app/ports.js";
import type { DeskState } from "../Studio/studioDesk.js";

type ProjectForm = "course" | "free";

// One line of the card's preview.
type OutlineEntry = {
    readonly title: string;
    readonly container: boolean;
};

type ProjectSummary = {
    readonly id: string;
    readonly title: string;
    readonly archived: boolean;
    readonly form: ProjectForm;
    readonly modules: number;
    readonly pages: number;
    readonly outline: readonly OutlineEntry[];
    // ISO 8601; absent: nothing in it has a time yet.
    readonly changedAt?: string;
    // Open suggestions in it.
    readonly waiting: number;
    // The page shown when the author left it (its title); absent: none yet,
    // or the project no longer holds it.
    readonly youWereIn?: string;
};

// A project's tree is cut off here (a ceiling, not a design size).
const TREE_MAX = 2000;
// The card's preview shows at most this many lines.
const OUTLINE_MAX = 3;

const newest = (a: string | undefined, b: string | undefined) =>
    a === undefined ? b : b === undefined ? a : a > b ? a : b;

// The materials in a project, root first, each once.
const treeOf = (
    roots: readonly MaterialItem[],
    byId: ReadonlyMap<string, MaterialItem>,
): readonly MaterialItem[] => {
    // Marked when queued, so each material enters the queue once.
    const seen = new Set<string>(roots.map((m) => m.id));
    const queue = [...new Map(roots.map((m) => [m.id, m])).values()];
    for (let at = 0; at < queue.length && at < TREE_MAX; at++) {
        const next = queue[at];
        if (next === undefined) continue;
        for (const id of next.parts ?? []) {
            const part = byId.get(id);
            if (part === undefined || seen.has(id)) continue;
            seen.add(id);
            queue.push(part);
        }
    }
    const out = queue.slice(0, TREE_MAX);
    return out;
};

const titleOf = (m: MaterialItem): string => m.title ?? "";

// In the Library's order; null (not loaded yet) is none. `places`: where the
// author was in each project (by project id).
const projectsOf = (
    library: Library | null,
    places: ReadonlyMap<string, DeskState> = new Map(),
): readonly ProjectSummary[] => {
    if (library === null) return [];
    const byId = new Map(library.materials.map((m) => [m.id, m]));
    const waitingOf = new Map(
        library.issues.map((i) => [i.resourceId, i.count]),
    );
    return (library.projects ?? []).map((p): ProjectSummary => {
        const roots = p.canvas.flatMap((item) => {
            const m = byId.get(item.target);
            return m === undefined ? [] : [m];
        });
        const tree = treeOf(roots, byId);
        const courses = roots.filter((m) => m.container?.kind === "course");
        const [course] = courses;
        const form: ProjectForm =
            course !== undefined && roots.length === 1 ? "course" : "free";
        const shown =
            form === "course" && course !== undefined
                ? (course.parts ?? []).flatMap((id) => {
                      const m = byId.get(id);
                      return m === undefined ? [] : [m];
                  })
                : roots;
        const inTree = new Set(tree.map((m) => m.id));
        const page = places.get(p.id)?.path.at(-1);
        const pageItem =
            page === undefined || !inTree.has(page.id)
                ? undefined
                : byId.get(page.id);
        const changedAt = tree.reduce<string | undefined>(
            (at, m) => newest(at, m.lastEditedAt ?? m.createdAt),
            undefined,
        );
        return {
            id: p.id,
            title: p.title,
            archived: p.lifecycle === "archived",
            form,
            modules: tree.filter((m) => m.container?.kind === "module").length,
            pages: tree.filter((m) => m.container === undefined).length,
            outline: shown.slice(0, OUTLINE_MAX).map((m) => ({
                title: titleOf(m),
                container: m.container !== undefined,
            })),
            ...(changedAt === undefined ? {} : { changedAt }),
            waiting: tree.reduce((n, m) => n + (waitingOf.get(m.id) ?? 0), 0),
            ...(pageItem === undefined || titleOf(pageItem) === ""
                ? {}
                : { youWereIn: titleOf(pageItem) }),
        };
    });
};
